import { useEffect, useState } from "react";
import api from "@/lib/api";
import { FileText, MapPin, Clock } from "lucide-react";

const DOC_LABELS = {
  customer_tos: "Customer Terms of Service",
  vendor_agreement: "Vendor Agreement",
  privacy_policy: "Privacy Policy",
  payment_policy: "Platform Payment Policy",
  vendor_policy_snapshot: "Vendor Cancellation & Deposit Terms (booking snapshot)",
};

function formatDT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}

/**
 * Displays a booking's immutable acceptance history.
 * Props:
 *   bookingId (required)
 *   compact (bool) — smaller, no title
 */
export default function AcceptanceLog({ bookingId, compact = false }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!bookingId) return;
    api.get(`/legal-acceptances/booking/${bookingId}`)
      .then(({ data }) => setRows(data))
      .catch((e) => setError(e.response?.status === 403 ? "You are not authorized to view this log." : "Couldn't load acceptance log."));
  }, [bookingId]);

  if (error) return <div className="text-xs text-[color:var(--text-muted)]" data-testid={`acceptance-log-error-${bookingId}`}>{error}</div>;
  if (rows === null) return <div className="text-xs text-[color:var(--text-muted)]">Loading acceptance log…</div>;

  return (
    <div data-testid={`acceptance-log-${bookingId}`}>
      {!compact && (
        <div className="flex items-center gap-2 mb-3">
          <FileText className="w-4 h-4 text-[color:var(--text-muted)]" />
          <div className="eyebrow">Legal acceptance history</div>
        </div>
      )}
      {rows.length === 0 ? (
        <div className="text-xs text-[color:var(--text-muted)]" data-testid={`acceptance-log-empty-${bookingId}`}>
          No acceptance records for this booking yet.
        </div>
      ) : (
        <ul className="space-y-2 text-xs" data-testid={`acceptance-log-list-${bookingId}`}>
          {rows.map((r) => (
            <li
              key={r.acceptance_id}
              data-testid={`acceptance-${r.acceptance_id}`}
              className="rounded-md border border-[color:var(--border)] bg-white p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-[color:var(--text)]">
                    {DOC_LABELS[r.document_type] || r.document_type}
                    {r.document_version && <span className="ml-1 text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">v{r.document_version}</span>}
                  </div>
                  <div className="text-[11px] text-[color:var(--text-muted)] mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDT(r.accepted_at)}</span>
                    {r.ip_address && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.ip_address}</span>}
                    <span>· {r.user_role}</span>
                    <span>· via {r.source}</span>
                  </div>
                </div>
              </div>
              {r.policy_snapshot?.computed && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-[color:var(--accent)] text-[11px] link-underline">
                    View snapshotted terms
                  </summary>
                  <div className="mt-2 space-y-1 text-[11px] text-[color:var(--text-muted)] leading-relaxed">
                    {r.policy_snapshot.computed.deposit_summary && <div><strong className="text-[color:var(--text)]">Deposit:</strong> {r.policy_snapshot.computed.deposit_summary}</div>}
                    {r.policy_snapshot.computed.balance_summary && <div><strong className="text-[color:var(--text)]">Balance:</strong> {r.policy_snapshot.computed.balance_summary}</div>}
                    {r.policy_snapshot.computed.cancellation_summary && <div><strong className="text-[color:var(--text)]">Cancellation:</strong> {r.policy_snapshot.computed.cancellation_summary}</div>}
                    {r.policy_snapshot.rescheduling_policy && <div><strong className="text-[color:var(--text)]">Rescheduling:</strong> {r.policy_snapshot.rescheduling_policy}</div>}
                    {r.policy_snapshot.weather_policy && <div><strong className="text-[color:var(--text)]">Weather:</strong> {r.policy_snapshot.weather_policy}</div>}
                    {r.policy_snapshot.other_terms && <div><strong className="text-[color:var(--text)]">Other:</strong> {r.policy_snapshot.other_terms}</div>}
                  </div>
                </details>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
